import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import {
  checkCanPurchasePass,
  selectCanPurchaseChecked,
  selectCanPurchaseLoading,
  selectCanPurchaseNewPass,
} from "../../../store/multipleEntryPassSlice";
import { selectPartner } from "../../../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import { getPartnerAsset } from "../../theme/partnerAssets";
import "./discoverPassCard.scss";

// V2 "תכירו את הכרטיסייה שלנו" pass-card promo (/discover-pass-card) — hero with
// the partner's pass artwork over a sheet carrying the pitch, a purchase CTA and
// a link to the /how-it-works explainer. Formerly the /how-it-works screen; no
// in-app entry point yet (product TBD), so it is reachable by direct URL only.

// Same path as ScreenHeader's variant="close" X; inlined because this screen has
// no header bar — the control floats over the gradient hero.
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function DiscoverPassCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const partner = useSelector(selectPartner);
  const canPurchaseNewPass = useSelector(selectCanPurchaseNewPass);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  const canPurchaseLoading = useSelector(selectCanPurchaseLoading);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);

  // Self-heal for a direct URL hit, where home never ran the purchase-eligibility
  // check — same pattern as profile's useProfileData.
  useEffect(() => {
    if (canPurchaseChecked || canPurchaseLoading) return;
    if (!mainUserParams?.Token) return;
    dispatch(
      checkCanPurchasePass({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      }),
    );
  }, [canPurchaseChecked, canPurchaseLoading, mainUserParams?.Token, dispatch]);

  // Direct URL hit by a partner outside the rollout → home.
  if (!isV2Enabled(V2_SCREEN_KEYS.DISCOVER_PASS_CARD, partner)) return <Navigate to="/" replace />;

  // Server said this user may not buy a pass → the CTA stays visible but dead,
  // matching the promo banners that hide entirely.
  const buyDisabled = canPurchaseChecked && canPurchaseNewPass === false;

  const passArt = getPartnerAsset(partner, "passCardImg");

  return (
    <div className="dpc-v2">
      <div className="dpc-v2-hero">
        <button type="button" className="dpc-v2-close" onClick={() => navigate(-1)} aria-label="close">
          <CloseIcon />
        </button>
        {/* The artwork carries the card gradient, its title and the freefit mark — no overlays.
            Two faded copies peek out below the main card for the stacked-deck effect. */}
        {passArt && (
          <div className="dpc-v2-hero-stack">
            <img src={passArt} alt="" aria-hidden="true" className="dpc-v2-hero-art dpc-v2-hero-art--back" />
            <img src={passArt} alt="" aria-hidden="true" className="dpc-v2-hero-art dpc-v2-hero-art--mid" />
            <img src={passArt} alt="" className="dpc-v2-hero-art" />
          </div>
        )}
      </div>

      <div className="dpc-v2-sheet">
        <p className="dpc-v2-title">{t("DISCOVER_PASS_TITLE")}</p>
        <p className="dpc-v2-body">{t("DISCOVER_PASS_BODY")}</p>

        <div className="dpc-v2-actions">
          <button
            type="button"
            className="dpc-v2-cta"
            disabled={buyDisabled}
            onClick={() => navigate("/multipleEntryPassPurchase")}
          >
            {t("HOW_IT_WORKS_BUY_PASS_CTA")}
          </button>
          <button type="button" className="dpc-v2-link" onClick={() => navigate("/how-it-works")}>
            {t("DISCOVER_PASS_HOW_LINK")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DiscoverPassCard;
